import {
    Alert,
    Badge,
    Breadcrumb,
    Button,
    Card,
    Col,
    Form,
    Row,
    Spinner
} from "react-bootstrap";
import LoggedInLayout from "../../layouts/loggedin-layout";
import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useForm, useWatch } from "react-hook-form";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faArrowUp,
    faChevronDown,
    faChevronUp,
    faFloppyDisk
} from "@fortawesome/free-solid-svg-icons";
import {
    useManageGroupAppDefinitionsQuery,
    useManageGroupAppRoleQuery,
    useManageGroupAppUpdate
} from "../../new/query";
import NonExpandableSearchBox from "../../components/nonexpandablesearchbox";
import styles from "../../styles/managegroupappnew.module.css";

function useMediaQuery(query) {
    const getMatches = () =>
        typeof window !== "undefined" ? window.matchMedia(query).matches : false;

    const [matches, setMatches] = useState(getMatches);

    useEffect(() => {
        const mediaQueryList = window.matchMedia(query);
        const handleChange = (event) => {
            setMatches(event.matches);
        };

        setMatches(mediaQueryList.matches);
        mediaQueryList.addEventListener("change", handleChange);

        return () => {
            mediaQueryList.removeEventListener("change", handleChange);
        };
    }, [query]);

    return matches;
}

const ManageGroupAppNew = () => {
    const [searchTerm, setSearchTerm] = useState("");
    const [dockRoot, setDockRoot] = useState(null);
    const [isStatusDockOpen, setIsStatusDockOpen] = useState(false);
    const [baselineStatuses, setBaselineStatuses] = useState({});
    const [dockFeedback, setDockFeedback] = useState(null);

    const isMobileViewport = useMediaQuery("(max-width: 991.98px)");

    const {
        control,
        register,
        handleSubmit,
        reset,
        setValue,
        watch,
        formState: { isSubmitting, isDirty }
    } = useForm({
        defaultValues: {
            roleid: "",
            statuses: {}
        }
    });

    useEffect(() => {
        document.title = "MFP - Edit Group App Status";
    }, []);

    useEffect(() => {
        setDockRoot(document.body);
    }, []);

    const selectedRoleId = watch("roleid");
    const definitionsQuery = useManageGroupAppDefinitionsQuery();
    const roleQuery = useManageGroupAppRoleQuery(selectedRoleId);
    const updateMutation = useManageGroupAppUpdate(
        selectedRoleId,
        () => {
            setDockFeedback({
                type: "success",
                message: "Application statuses saved successfully."
            });
        },
        (error) => {
            setDockFeedback({
                type: "error",
                message: error?.message || "A problem occured when updating the application statuses."
            });
        }
    );
    const watchedStatuses = useWatch({
        control,
        name: "statuses",
        defaultValue: {}
    });
    const roles = definitionsQuery.data?.roles ?? [];
    const statuses = definitionsQuery.data?.applicationstatustypes ?? [];
    const groups = definitionsQuery.data?.applicationstatustypegroups ?? [];
    const isLoading = definitionsQuery.isLoading && !definitionsQuery.data;
    const loadingOptions = roleQuery.isFetching;

    useEffect(() => {
        if (!selectedRoleId) {
            reset({ roleid: "", statuses: {} });
            setSearchTerm("");
            setIsStatusDockOpen(false);
            setBaselineStatuses({});
            setDockFeedback(null);
            return;
        }

        reset({ roleid: selectedRoleId, statuses: {} });
        setBaselineStatuses({});
        setSearchTerm("");
        setDockFeedback(null);
    }, [selectedRoleId, reset]);

    useEffect(() => {
        if (!selectedRoleId || !roleQuery.data) {
            return;
        }

        const nextStatuses = statuses.reduce((acc, statusItem) => {
            acc[statusItem.id] = roleQuery.data.statuses.includes(statusItem.id);
            return acc;
        }, {});

        reset({ roleid: selectedRoleId, statuses: nextStatuses });
        setBaselineStatuses(nextStatuses);
        setSearchTerm("");
    }, [roleQuery.data, reset, selectedRoleId, statuses]);

    useEffect(() => {
        if (!isDirty) {
            return;
        }

        setDockFeedback(null);
    }, [isDirty, watchedStatuses]);

    useEffect(() => {
        if (dockFeedback || roleQuery.error) {
            setIsStatusDockOpen(true);
        }
    }, [dockFeedback, roleQuery.error]);

    const statusByGroup = useMemo(() => {
        const map = {};
        groups.forEach((group) => {
            map[group.id] = [];
        });

        statuses.forEach((statusItem) => {
            if (!map[statusItem.groupid]) {
                map[statusItem.groupid] = [];
            }

            map[statusItem.groupid].push(statusItem);
        });

        return map;
    }, [statuses, groups]);

    const normalizedSearch = searchTerm.trim().toLowerCase();

    const filteredGroups = useMemo(() => {
        return groups
            .map((group) => {
                const allStatuses = statusByGroup[group.id] || [];
                const visibleStatuses = normalizedSearch
                    ? allStatuses.filter((statusItem) => statusItem.name.toLowerCase().includes(normalizedSearch))
                    : allStatuses;

                const selectedCount = allStatuses.filter((statusItem) => watchedStatuses?.[statusItem.id]).length;

                return {
                    ...group,
                    allStatuses,
                    visibleStatuses,
                    selectedCount
                };
            })
            .filter((group) => normalizedSearch ? group.visibleStatuses.length > 0 : true);
    }, [groups, normalizedSearch, statusByGroup, watchedStatuses]);

    const selectedCount = useMemo(() => {
        return statuses.filter((statusItem) => watchedStatuses?.[statusItem.id]).length;
    }, [statuses, watchedStatuses]);

    const visibleStatuses = useMemo(() => {
        return filteredGroups.flatMap((group) => group.visibleStatuses);
    }, [filteredGroups]);

    const pendingSelectedCount = useMemo(() => {
        return statuses.filter((statusItem) => {
            const currentValue = !!watchedStatuses?.[statusItem.id];
            const baselineValue = !!baselineStatuses?.[statusItem.id];

            return currentValue && !baselineValue;
        }).length;
    }, [baselineStatuses, statuses, watchedStatuses]);

    const pendingUnselectedCount = useMemo(() => {
        return statuses.filter((statusItem) => {
            const currentValue = !!watchedStatuses?.[statusItem.id];
            const baselineValue = !!baselineStatuses?.[statusItem.id];

            return !currentValue && baselineValue;
        }).length;
    }, [baselineStatuses, statuses, watchedStatuses]);

    const applyStatusSelection = (statusItems, value) => {
        statusItems.forEach((statusItem) => {
            setValue(`statuses.${statusItem.id}`, value, { shouldDirty: true });
        });
    };

    const scrollToTop = () => {
        window.scrollTo({
            top: 0,
            behavior: "smooth"
        });
    };

    const onSubmit = async (values) => {
        const statusIds = Object.entries(values.statuses)
            .filter(([_id, isSelected]) => isSelected)
            .map(([id]) => id);

        const submitData = {
            RoleID: selectedRoleId,
            Statuses: statusIds
        };

        try {
            const data = await updateMutation.mutateAsync(submitData);
            const nextStatuses = statuses.reduce((acc, statusItem) => {
                acc[statusItem.id] = data.statuses.includes(statusItem.id);
                return acc;
            }, {});

            reset({ roleid: selectedRoleId, statuses: nextStatuses });
            setBaselineStatuses(nextStatuses);
        } catch (_error) {
            // Dock feedback is handled by the mutation error callback.
        }
    };

    if (isLoading) {
        return (
            <LoggedInLayout>
                <div className={styles.pageShell}>
                    <div className={styles.loadingHero}>
                        <div className={styles.loadingTitle}></div>
                        <div className={styles.loadingSubtitle}></div>
                    </div>
                    <div className={styles.loadingToolbar}></div>
                    <div className={styles.loadingGrid}>
                        <div className={styles.loadingCard}></div>
                        <div className={styles.loadingCardTall}></div>
                        <div className={styles.loadingCardMedium}></div>
                        <div className={styles.loadingCard}></div>
                    </div>
                </div>
            </LoggedInLayout>
        );
    }

    const statusDock = selectedRoleId ? (
        <div className={styles.statusDock}>
            {isStatusDockOpen ? (
                <div className={styles.statusDockPanel}>
                    <div className={styles.statusDockHeader}>
                        <div>
                            <div className={styles.statusDockEyebrow}>Status editor</div>
                            <div className={styles.statusDockRole}>
                                {roles.find((role) => String(role.id) === String(selectedRoleId))?.name || "Selected role"}
                            </div>
                        </div>
                        <button
                            type="button"
                            className={styles.statusDockIconButton}
                            onClick={() => setIsStatusDockOpen(false)}
                            aria-label="Hide status editor dock"
                        >
                            <FontAwesomeIcon icon={faChevronDown} />
                        </button>
                    </div>

                    <div className={styles.statusDockStats}>
                        <div className={styles.statusDockStat}>
                            <span className={styles.statusDockValue}>{pendingSelectedCount}</span>
                            <span className={styles.statusDockLabel}>Pending select</span>
                        </div>
                        <div className={styles.statusDockStat}>
                            <span className={styles.statusDockValue}>{pendingUnselectedCount}</span>
                            <span className={styles.statusDockLabel}>Pending clear</span>
                        </div>
                        <div className={styles.statusDockStat}>
                            <span className={styles.statusDockValue}>{pendingSelectedCount + pendingUnselectedCount}</span>
                            <span className={styles.statusDockLabel}>Unsaved total</span>
                        </div>
                    </div>

                    <div className={styles.statusDockActions}>
                        {isMobileViewport ? (
                            <Button
                                type="button"
                                variant="outline-secondary"
                                className={styles.statusDockActionButton}
                                onClick={scrollToTop}
                            >
                                <FontAwesomeIcon icon={faArrowUp} />
                                <span>Top</span>
                            </Button>
                        ) : null}
                        <Button
                            type="button"
                            variant="outline-primary"
                            className={styles.statusDockActionButton}
                            onClick={() => applyStatusSelection(visibleStatuses, true)}
                            disabled={loadingOptions || isSubmitting || visibleStatuses.length === 0}
                        >
                            Select visible
                        </Button>
                        <Button
                            type="button"
                            variant="outline-secondary"
                            className={styles.statusDockActionButton}
                            onClick={() => applyStatusSelection(visibleStatuses, false)}
                            disabled={loadingOptions || isSubmitting || visibleStatuses.length === 0}
                        >
                            Clear visible
                        </Button>
                        <Button
                            type="button"
                            variant="primary"
                            className={styles.statusDockSaveButton}
                            onClick={handleSubmit(onSubmit)}
                            disabled={loadingOptions || updateMutation.isPending || !isDirty || isSubmitting || !!roleQuery.error}
                        >
                            {isSubmitting || updateMutation.isPending ? <Spinner animation="border" size="sm" /> : <FontAwesomeIcon icon={faFloppyDisk} />}
                            <span>{isSubmitting || updateMutation.isPending ? "Saving..." : "Save changes"}</span>
                        </Button>
                    </div>
                    {dockFeedback ? (
                        <div
                            className={`${styles.statusDockFeedback} ${dockFeedback.type === "error" ? styles.statusDockFeedbackError : styles.statusDockFeedbackSuccess}`}
                            role="status"
                            aria-live="polite"
                        >
                            {dockFeedback.message}
                        </div>
                    ) : null}
                    {roleQuery.error ? (
                        <div className={`${styles.statusDockFeedback} ${styles.statusDockFeedbackError}`} role="alert">
                            {roleQuery.error?.message || "Unable to load statuses for this role."}
                        </div>
                    ) : null}
                </div>
            ) : null}

            <button
                type="button"
                className={`${styles.statusDockToggle} ${dockFeedback?.type === "success" ? styles.statusDockToggleSuccess : ""} ${dockFeedback?.type === "error" || roleQuery.error ? styles.statusDockToggleError : ""} ${isStatusDockOpen ? styles.statusDockToggleHidden : ""}`}
                onClick={() => setIsStatusDockOpen(true)}
                aria-label="Show status editor dock"
            >
                <span className={styles.statusDockToggleText}>
                    {dockFeedback
                        ? dockFeedback.type === "error"
                            ? "Save failed"
                            : "Saved"
                        : roleQuery.error
                            ? "Role load failed"
                            : `+${pendingSelectedCount} / -${pendingUnselectedCount} unsaved`}
                </span>
                <FontAwesomeIcon icon={faChevronUp} />
            </button>
        </div>
    ) : null;

    return (
        <LoggedInLayout>
            <div className={styles.pageShell}>
                <Row className="mb-4">
                    <Col xs={12}>
                        <div className={styles.hero}>
                            <div className={styles.heroHeading}>
                                <div>
                                    <h3 className={styles.title}>Edit Group App Status</h3>
                                    <Breadcrumb className={styles.breadcrumbs}>
                                        <Breadcrumb.Item className="primarybreadcrumb">Admin</Breadcrumb.Item>
                                        <Breadcrumb.Item className="secondarybreadcrumb" active>Edit or Update Group App Status</Breadcrumb.Item>
                                    </Breadcrumb>
                                </div>
                                <p className={styles.subtitle}>
                                    Select a role, narrow the list when needed, and update status access in grouped sections that are easier to scan on desktop and mobile.
                                </p>
                            </div>
                        </div>
                    </Col>
                </Row>

                <Row>
                    {definitionsQuery.error ? (
                        <Col xs={12}>
                            <Alert variant="danger" className={styles.pageAlert}>
                                <Alert.Heading>
                                    <h4>Unable to load group app status data.</h4>
                                </Alert.Heading>
                                <p>{definitionsQuery.error?.message || "An unexpected error occured while loading the page."}</p>
                                <Button type="button" variant="outline-danger" onClick={() => definitionsQuery.refetch()}>
                                    Retry
                                </Button>
                            </Alert>
                        </Col>
                    ) : groups.length === 0 || roles.length === 0 ? (
                        <Col xs={12}>
                            <Alert variant="danger">
                                <Alert.Heading>
                                    <h4>No Roles/Groups found.</h4>
                                </Alert.Heading>
                                <p>No Roles/Groups were found. Please add them via the Role Management Page.</p>
                            </Alert>
                        </Col>
                    ) : (
                        <Col xs={12}>
                            <Card className={styles.toolbarCard}>
                                <Card.Body>
                                    <input type="hidden" {...register("roleid")} />
                                    <Row className="g-3">
                                        <Col xs={12}>
                                            <div className={styles.rolePickerHeader}>
                                                <div>
                                                    <Form.Label className={styles.fieldLabel}>Role</Form.Label>
                                                    <p className={styles.rolePickerCopy}>
                                                        Choose the role you want to edit. The status groups below will update immediately.
                                                    </p>
                                                </div>
                                                {loadingOptions && <Spinner animation="border" size="sm" variant="primary" />}
                                            </div>
                                            <div className={styles.rolePillGrid}>
                                                {roles.map((role) => {
                                                    const isActive = String(selectedRoleId) === String(role.id);

                                                    return (
                                                        <button
                                                            key={role.id}
                                                            type="button"
                                                            className={`${styles.rolePill} ${isActive ? styles.rolePillActive : ""}`}
                                                            onClick={() => setValue("roleid", String(role.id), { shouldDirty: false })}
                                                            disabled={loadingOptions || isSubmitting}
                                                            aria-pressed={isActive}
                                                        >
                                                            <span className={styles.rolePillLabel}>{role.name}</span>
                                                            <span className={styles.rolePillMeta}>
                                                                {isActive ? "Editing" : "Select"}
                                                            </span>
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                        </Col>

                                        {selectedRoleId && (
                                            <>
                                                <Col xs={12} lg={7}>
                                                    <Form.Label htmlFor="status-search" className={styles.fieldLabel}>Find a status</Form.Label>
                                                    <NonExpandableSearchBox
                                                        id="status-search"
                                                        searchTerm={searchTerm}
                                                        setSearchTerm={setSearchTerm}
                                                        value={searchTerm}
                                                        placeholder="Search statuses by name..."
                                                        onChange={(event) => setSearchTerm(event.target.value)}
                                                        disabled={loadingOptions || isSubmitting}
                                                        aria-label="Find a status"
                                                        aria-describedby="status-search-hint"
                                                        aria-controls="status-results"
                                                        clearButtonLabel="Clear status search"
                                                        containerClassName={styles.searchField}
                                                        inputClassName={styles.searchFieldInput}
                                                        searchIconClassName={styles.searchFieldIcon}
                                                        clearButtonClassName={styles.searchFieldClearButton}
                                                    />
                                                    <p id="status-search-hint" className={styles.searchFieldHint}>
                                                        Filter status names instantly. Press Escape to clear. Showing {visibleStatuses.length} visible status{visibleStatuses.length === 1 ? "" : "es"} across {filteredGroups.length} group{filteredGroups.length === 1 ? "" : "s"}.
                                                    </p>
                                                </Col>
                                                <Col xs={12} lg={5}>
                                                    <div className={styles.summaryGrid}>
                                                        <div className={styles.summaryTile}>
                                                            <span className={styles.summaryValue}>{selectedCount}</span>
                                                            <span className={styles.summaryLabel}>Selected</span>
                                                        </div>
                                                        <div className={styles.summaryTile}>
                                                            <span className={styles.summaryValue}>{statuses.length}</span>
                                                            <span className={styles.summaryLabel}>Total statuses</span>
                                                        </div>
                                                        <div className={styles.summaryTile}>
                                                            <span className={styles.summaryValue}>{filteredGroups.length}</span>
                                                            <span className={styles.summaryLabel}>{normalizedSearch ? "Visible groups" : "Groups"}</span>
                                                        </div>
                                                    </div>
                                                </Col>
                                            </>
                                        )}
                                    </Row>
                                </Card.Body>
                            </Card>
                        </Col>
                    )}
                </Row>

                {selectedRoleId ? (
                    <Row className="mt-4">
                        {roleQuery.error ? (
                            <Col xs={12}>
                                <Alert id="status-results" variant="danger" className={styles.pageAlert}>
                                    <Alert.Heading>
                                        <h4>Unable to load this role's statuses.</h4>
                                    </Alert.Heading>
                                    <p>{roleQuery.error?.message || "Please try again."}</p>
                                    <Button type="button" variant="outline-danger" onClick={() => roleQuery.refetch()}>
                                        Retry
                                    </Button>
                                </Alert>
                            </Col>
                        ) : loadingOptions && !roleQuery.data ? (
                            <Col xs={12}>
                                <Card id="status-results" className={styles.bulkActionsCard}>
                                    <Card.Body className={styles.loadingRoleCard}>
                                        <Spinner animation="border" size="sm" />
                                        <span>Loading statuses for this role...</span>
                                    </Card.Body>
                                </Card>
                            </Col>
                        ) : roleQuery.data ? (
                            <>
                                <Col xs={12}>
                                    <Card className={styles.bulkActionsCard}>
                                        <Card.Body className={styles.bulkActionsBody}>
                                            <div>
                                                <div className={styles.bulkActionTitle}>Bulk actions</div>
                                                <div className={styles.bulkActionCopy}>
                                                    {normalizedSearch
                                                        ? `Applying actions to ${visibleStatuses.length} visible status${visibleStatuses.length === 1 ? "" : "es"}.`
                                                        : "Applying actions to all statuses currently shown on the page."}
                                                </div>
                                            </div>
                                            <div className={styles.bulkButtons}>
                                                <Button
                                                    type="button"
                                                    variant="outline-primary"
                                                    onClick={() => applyStatusSelection(visibleStatuses, true)}
                                                    disabled={loadingOptions || updateMutation.isPending || isSubmitting || visibleStatuses.length === 0 || !!roleQuery.error}
                                                >
                                                    Select visible
                                                </Button>
                                                <Button
                                                    type="button"
                                                    variant="outline-secondary"
                                                    onClick={() => applyStatusSelection(visibleStatuses, false)}
                                                    disabled={loadingOptions || updateMutation.isPending || isSubmitting || visibleStatuses.length === 0 || !!roleQuery.error}
                                                >
                                                    Clear visible
                                                </Button>
                                            </div>
                                        </Card.Body>
                                    </Card>
                                </Col>

                                {filteredGroups.length === 0 ? (
                                    <Col xs={12} className="mt-3">
                                        <Alert id="status-results" variant="light" className={styles.emptyState}>
                                            No statuses matched your search.
                                        </Alert>
                                    </Col>
                                ) : (
                                    <Col xs={12} className="mt-3">
                                        <div id="status-results" className={styles.groupGrid}>
                                            {filteredGroups.map((group) => (
                                                <div className={styles.groupGridItem} key={group.id}>
                                                    <Card className={styles.groupCard}>
                                                        <Card.Body>
                                                            <div className={styles.groupHeader}>
                                                                <div>
                                                                    <div className={styles.groupTitleRow}>
                                                                        <h4 className={styles.groupTitle}>{group.name}</h4>
                                                                        <Badge bg="light" text="dark" pill>
                                                                            {group.selectedCount}/{group.allStatuses.length} selected
                                                                        </Badge>
                                                                    </div>
                                                                    <p className={styles.groupMeta}>
                                                                        {group.visibleStatuses.length} visible status{group.visibleStatuses.length === 1 ? "" : "es"}
                                                                        {normalizedSearch ? ` matching "${searchTerm.trim()}"` : ""}
                                                                    </p>
                                                                </div>
                                                                <div className={styles.groupActions}>
                                                                    <Button
                                                                        type="button"
                                                                        size="sm"
                                                                        variant="outline-primary"
                                                                        onClick={() => applyStatusSelection(group.visibleStatuses, true)}
                                                                        disabled={loadingOptions || updateMutation.isPending || isSubmitting || group.visibleStatuses.length === 0 || !!roleQuery.error}
                                                                    >
                                                                        Select group
                                                                    </Button>
                                                                    <Button
                                                                        type="button"
                                                                        size="sm"
                                                                        variant="outline-secondary"
                                                                        onClick={() => applyStatusSelection(group.visibleStatuses, false)}
                                                                        disabled={loadingOptions || updateMutation.isPending || isSubmitting || group.visibleStatuses.length === 0 || !!roleQuery.error}
                                                                    >
                                                                        Clear group
                                                                    </Button>
                                                                </div>
                                                            </div>

                                                            <div className={styles.statusList}>
                                                                {group.visibleStatuses.map((statusItem) => (
                                                                    <label key={statusItem.id} htmlFor={`statuses.${statusItem.id}`} className={styles.statusRow}>
                                                                        <div className={styles.statusCopy}>
                                                                            <span className={styles.statusName}>{statusItem.name}</span>
                                                                        </div>
                                                                        <Form.Check
                                                                            type="checkbox"
                                                                            id={`statuses.${statusItem.id}`}
                                                                            className={styles.statusCheck}
                                                                            disabled={loadingOptions || updateMutation.isPending || isSubmitting || !!roleQuery.error}
                                                                            {...register(`statuses.${statusItem.id}`)}
                                                                        />
                                                                    </label>
                                                                ))}
                                                            </div>
                                                        </Card.Body>
                                                    </Card>
                                                </div>
                                            ))}
                                        </div>
                                    </Col>
                                )}
                            </>
                        ) : (
                            <Col xs={12} className="mt-3">
                                <Alert variant="light" className={styles.emptyState}>
                                    Select a role to begin editing statuses.
                                </Alert>
                            </Col>
                        )}

                    </Row>
                ) : null}
            </div>
            {dockRoot ? createPortal(statusDock, dockRoot) : null}
        </LoggedInLayout>
    );
};

export default ManageGroupAppNew;
